// Function index:
//   renderOverlays(ctx) — clear + redraw the adjacency / current / hover rings
//       based on the current self position and hovered key. Pure visual helper;
//       owns no state itself.

import type { Graphics } from 'pixi.js';
import { NEIGHBOURS, isAdjacent, pathHex } from './palette.js';

export interface OverlayCtx {
  adjacencyRing: Graphics;
  currentRing: Graphics;
  hoverRing: Graphics;
  currentQ: number;
  currentR: number;
  hoverKey: string | null;
  // Hex (q,r) → its outline corners in the active projection. hex-scene.ts
  // supplies the flat pointy-top HexTile corners or (under iso)
  // iso/project.ts's isoHexCorners — overlays.ts stays projection-agnostic.
  cornersFor: (q: number, r: number) => Array<{ x: number; y: number }>;
}

// Phase B3: no Grid dep — overlays compute hex corners on the fly from
// (q, r) via ctx.cornersFor. We always draw all three rings around the
// player's current position; whether the underlying tile is loaded or
// rendered is irrelevant to the overlay (the overlay floats above
// regardless).
export function renderOverlays(ctx: OverlayCtx): void {
  ctx.adjacencyRing.clear();
  ctx.currentRing.clear();
  ctx.hoverRing.clear();

  for (const [dq, dr] of NEIGHBOURS) {
    pathHex(ctx.adjacencyRing, ctx.cornersFor(ctx.currentQ + dq, ctx.currentR + dr));
    ctx.adjacencyRing.stroke({ color: 0xffeb88, width: 1.5, alpha: 0.5 });
  }
  pathHex(ctx.currentRing, ctx.cornersFor(ctx.currentQ, ctx.currentR));
  ctx.currentRing.stroke({ color: 0xffeb88, width: 3 });
  if (ctx.hoverKey) {
    const [hq, hr] = ctx.hoverKey.split(',').map(Number) as [number, number];
    if (isAdjacent(hq, hr, ctx.currentQ, ctx.currentR)) {
      pathHex(ctx.hoverRing, ctx.cornersFor(hq, hr));
      ctx.hoverRing.fill({ color: 0xffeb88, alpha: 0.18 }).stroke({ color: 0xffeb88, width: 2 });
    }
  }
}
